import { useState } from "react";
import { SendTestEmailModal } from "@/components/email/SendTestEmailModal";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DocumentRequestTemplate,
  DocumentRequestTemplateComponent,
} from "@/components/email/templates/DocumentRequestTemplate";
import {
  AlignLeft,
  AlertCircle,
  CheckCircle2,
  Code,
  Copy,
  Download,
  Edit,
  Eye,
  FileText,
  Mail,
  Maximize2,
  MessageSquare,
  Monitor,
  Plus,
  RefreshCw,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  Smartphone,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";

// Mock data for demonstration
const templatesList = [
  {
    id: 1,
    name: "Document Request",
    description: "Request tax documents from clients",
    type: "transactional",
    lastUpdated: "2024-01-15",
    usageCount: 145,
  },
  {
    id: 2,
    name: "Information Request",
    description: "Request additional information from clients",
    type: "transactional",
    lastUpdated: "2024-01-18",
    usageCount: 87,
  },
  {
    id: 3,
    name: "Service Update",
    description: "Notify clients about tax service progress",
    type: "transactional",
    lastUpdated: "2024-01-20",
    usageCount: 112,
  },
  {
    id: 4,
    name: "Tax Return Completion",
    description: "Notify clients about completed tax returns",
    type: "transactional",
    lastUpdated: "2024-01-22",
    usageCount: 56,
  },
  {
    id: 5,
    name: "Welcome Email",
    description: "Welcome new clients to the platform",
    type: "transactional",
    lastUpdated: "2024-01-25",
    usageCount: 32,
  },
  {
    id: 6,
    name: "Password Reset",
    description: "Password reset instructions",
    type: "transactional",
    lastUpdated: "2024-01-26",
    usageCount: 18,
  },
];

export default function EmailTemplates() {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(1); // Default to first template
  const [isTestEmailModalOpen, setIsTestEmailModalOpen] = useState(false);
  const [currentTemplateData, setCurrentTemplateData] = useState<{
    name: string;
    html: string;
    text?: string;
  } | null>(null);

  // Filter templates based on search term and type filter
  const filteredTemplates = templatesList.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || template.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Get the template HTML and text content for the selected template
  const getTemplateContent = (templateId: number) => {
    // For now, we'll just use the DocumentRequestTemplate for all templates
    // In a real app, you would have different templates for each type
    const template = templatesList.find((t) => t.id === templateId);
    if (!template) return null;

    // This is a simplified example - in a real app, you would fetch the actual template content
    const documentRequestTemplate = new DocumentRequestTemplate({
      recipientName: "John Smith",
      documentType: "Income Statement",
      dueDate: "30 June 2024",
      taxAgentName: "Sarah Johnson",
    });
    return {
      name: template.name,
      html: documentRequestTemplate.htmlContent,
      text: documentRequestTemplate.textContent,
    };
  };

  const handleSendTestEmail = (templateId: number) => {
    const templateContent = getTemplateContent(templateId);
    if (templateContent) {
      setCurrentTemplateData(templateContent);
      setIsTestEmailModalOpen(true);
    }
  };

  return (
    <EmailPageLayout
      title="Email Templates"
      description="Manage and preview email templates"
    >
      <div className="relative bg-gradient-to-b from-primary/10 to-background py-8 overflow-hidden rounded-t-lg mb-8">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1596526131083-e8c633c948d2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
            alt="Email templates background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-background"></div>
        </div>
        <div className="relative z-10 px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-3xl font-bold mb-2">Email Templates</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Manage and preview compliant email templates with all required
              elements
            </p>
            <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-sm font-medium">
              <Server className="h-3.5 w-3.5 mr-1.5" />
              For AWS SES Review Team
            </div>
            <div className="flex items-center justify-center mt-4 text-sm text-muted-foreground">
              <Mail className="h-4 w-4 mr-2" />
              <span>
                Last updated:{" "}
                {new Date().toLocaleDateString("en-AU", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="px-4 py-6 max-w-5xl mx-auto space-y-8">
        <Alert className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900 shadow-sm">
          <MessageSquare className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          <AlertTitle className="text-yellow-800 dark:text-yellow-300 font-medium">
            AWS SES Compliant Templates
          </AlertTitle>
          <AlertDescription className="text-yellow-700 dark:text-yellow-400 mt-1">
            All email templates include required elements for AWS SES production
            access: physical mailing address, unsubscribe mechanism, and both
            HTML and plain text versions.
          </AlertDescription>
        </Alert>

        <div className="flex justify-between items-center mb-6">
          <div className="w-auto">
            <Tabs
              defaultValue="templates"
              className="w-full"
              onValueChange={(value) => {
                // You can add any additional logic here when tab changes
                console.log(`Tab changed to: ${value}`);
              }}
            >
              <div className="flex justify-between items-center">
                <TabsList className="h-10 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <TabsTrigger
                    value="templates"
                    className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-950 data-[state=active]:shadow-sm data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Templates
                  </TabsTrigger>
                  <TabsTrigger
                    value="preview"
                    className="rounded-md data-[state=active]:bg-white dark:data-[state=active]:bg-gray-950 data-[state=active]:shadow-sm data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </TabsTrigger>
                </TabsList>

                <Button className="bg-blue-600 hover:bg-blue-700 text-white ml-4">
                  <Plus className="h-4 w-4 mr-2" />
                  New Template
                </Button>
              </div>
              <TabsContent value="templates" className="space-y-4">
                <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm mb-6">
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div className="relative w-full md:w-auto">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <Input
                          placeholder="Search templates..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full md:w-[300px] pl-9 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700 focus:border-blue-500 dark:focus:border-blue-500"
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Select
                          value={typeFilter}
                          onValueChange={setTypeFilter}
                        >
                          <SelectTrigger className="w-full md:w-[180px] bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700">
                            <SelectValue placeholder="Filter by type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="transactional">
                              Transactional
                            </SelectItem>
                            <SelectItem value="marketing">Marketing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredTemplates.map((template) => (
                    <motion.div
                      key={template.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="h-full"
                    >
                      <Card
                        className={`cursor-pointer transition-all hover:shadow-md h-full flex flex-col bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 ${
                          selectedTemplate === template.id
                            ? "ring-1 ring-blue-500 border-blue-200 dark:border-blue-900 shadow-sm"
                            : ""
                        }`}
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        <div className="absolute top-2 right-2">
                          <Badge
                            variant="outline"
                            className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 text-xs"
                          >
                            {template.type}
                          </Badge>
                        </div>
                        <CardHeader className="pb-3 flex-shrink-0 pt-6">
                          <div className="flex items-center">
                            <CardTitle className="text-base flex items-center text-gray-900 dark:text-gray-100">
                              <MessageSquare className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" />
                              <span className="truncate">{template.name}</span>
                            </CardTitle>
                          </div>
                          <CardDescription className="line-clamp-2 text-gray-600 dark:text-gray-400 mt-1">
                            {template.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pb-3 flex-grow">
                          <div className="flex flex-col space-y-3">
                            <div className="flex flex-wrap justify-between text-sm gap-y-2">
                              <span className="text-gray-500 dark:text-gray-400 flex items-center mr-2">
                                <FileText className="h-3.5 w-3.5 mr-1.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="whitespace-nowrap">
                                  Last updated: {template.lastUpdated}
                                </span>
                              </span>
                              <span className="text-gray-500 dark:text-gray-400 flex items-center">
                                <Mail className="h-3.5 w-3.5 mr-1.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="whitespace-nowrap">
                                  Used: {template.usageCount} times
                                </span>
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-600 dark:text-gray-400 mt-1 pt-1 border-t border-gray-100 dark:border-gray-800">
                              <div className="flex items-center">
                                <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                                <span className="ml-1">HTML & Text</span>
                              </div>
                              <div className="flex items-center">
                                <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                                <span className="ml-1">Unsubscribe</span>
                              </div>
                              <div className="flex items-center">
                                <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                                <span className="ml-1">Physical address</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                        <CardFooter className="flex justify-between pt-3 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 bg-gray-50 dark:bg-gray-900">
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            className="gap-1 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleSendTestEmail(template.id)}
                          >
                            <Send className="h-4 w-4" />
                            Send
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="preview" className="space-y-6 mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-4 space-y-6">
                    <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                      <CardHeader className="pb-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                        <CardTitle className="text-base flex items-center text-gray-900 dark:text-gray-100">
                          <Settings className="h-4 w-4 mr-2 text-blue-500" />
                          Template Settings
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 space-y-4">
                        <div className="space-y-2">
                          <Label
                            htmlFor="template-select"
                            className="text-sm font-medium"
                          >
                            Select Template
                          </Label>
                          <Select
                            value={selectedTemplate?.toString() || "1"}
                            onValueChange={(value) =>
                              setSelectedTemplate(parseInt(value))
                            }
                          >
                            <SelectTrigger
                              id="template-select"
                              className="w-full bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700"
                            >
                              <SelectValue placeholder="Select a template" />
                            </SelectTrigger>
                            <SelectContent>
                              {templatesList.map((template) => (
                                <SelectItem
                                  key={template.id}
                                  value={template.id.toString()}
                                >
                                  <div className="flex items-center">
                                    <MessageSquare className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" />
                                    <span className="truncate">
                                      {template.name}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Preview Options
                          </Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-700 dark:text-gray-300 justify-start"
                            >
                              <Monitor className="h-4 w-4 flex-shrink-0 text-blue-500" />
                              <span>Desktop</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-700 dark:text-gray-300 justify-start"
                            >
                              <Smartphone className="h-4 w-4 flex-shrink-0 text-blue-500" />
                              <span>Mobile</span>
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Format</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-700 dark:text-gray-300 justify-start"
                            >
                              <Code className="h-4 w-4 flex-shrink-0 text-blue-500" />
                              <span>HTML</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1 bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 text-gray-700 dark:text-gray-300 justify-start"
                            >
                              <AlignLeft className="h-4 w-4 flex-shrink-0 text-blue-500" />
                              <span>Plain Text</span>
                            </Button>
                          </div>
                        </div>

                        <div className="pt-2">
                          <Button
                            className="w-full gap-1 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() =>
                              handleSendTestEmail(selectedTemplate || 1)
                            }
                          >
                            <Send className="h-4 w-4 flex-shrink-0" />
                            <span>Send Test Email</span>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-white dark:bg-gray-950 border-gray-100 dark:border-gray-800 shadow-sm">
                      <CardHeader className="pb-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                        <CardTitle className="text-base flex items-center text-gray-900 dark:text-gray-100">
                          <Shield className="h-4 w-4 mr-2 text-blue-500" />
                          Compliance
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <ul className="space-y-2">
                          <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                            Physical mailing address included
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                            Functional unsubscribe mechanism
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                            Both HTML and plain text versions
                          </li>
                          <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                            AWS SES compliant
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="lg:col-span-8">
                    <div className="relative">
                      <div className="sticky top-0 z-10 flex items-center justify-between p-3 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 rounded-t-lg">
                        <div className="flex items-center gap-3">
                          <div className="flex space-x-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Eye className="h-4 w-4 text-blue-400" />
                            <span className="text-sm font-medium text-gray-200">
                              Template Preview
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className="bg-yellow-950/30 text-yellow-400 border-yellow-900/50"
                          >
                            <Shield className="h-3 w-3 mr-1 flex-shrink-0" />
                            AWS SES Compliant
                          </Badge>
                          <Badge
                            variant="outline"
                            className="bg-blue-950/30 text-blue-400 border-blue-900/50"
                          >
                            Preview Mode
                          </Badge>
                        </div>
                      </div>

                      <div className="bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-800 rounded-lg shadow-xl overflow-hidden">
                        <div className="flex items-center justify-between p-2 border-b border-gray-800 bg-gray-900/80">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 text-gray-300 hover:text-white hover:bg-gray-800"
                            >
                              <RefreshCw className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="text-xs">Refresh</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 text-gray-300 hover:text-white hover:bg-gray-800"
                            >
                              <Maximize2 className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="text-xs">Fullscreen</span>
                            </Button>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 text-gray-300 hover:text-white hover:bg-gray-800"
                          >
                            <Edit className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="text-xs">Edit Template</span>
                          </Button>
                        </div>

                        <div className="p-4 overflow-auto max-h-[600px] bg-[url('https://images.unsplash.com/photo-1539721972319-f0e80a00d424?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80')] bg-cover bg-center">
                          <div className="bg-gray-950/80 backdrop-blur-sm p-6 rounded-lg border border-gray-800 shadow-lg">
                            <DocumentRequestTemplateComponent preview={true} />
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 border-t border-gray-800 bg-gray-900">
                          <div className="text-xs text-gray-400">
                            Last updated:{" "}
                            {new Date().toLocaleDateString("en-AU", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1 text-gray-300 hover:text-white hover:bg-gray-800"
                            >
                              <Download className="h-3.5 w-3.5 mr-1" />
                              <span className="text-xs">Export</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1 text-gray-300 hover:text-white hover:bg-gray-800"
                            >
                              <Copy className="h-3.5 w-3.5 mr-1" />
                              <span className="text-xs">Copy HTML</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-100 dark:border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center text-gray-900 dark:text-gray-100">
                <MessageSquare className="h-5 w-5 mr-2 text-blue-500" />
                Email Templates
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                All email templates are compliant with AWS SES requirements and
                include necessary elements for production access.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center text-gray-900 dark:text-gray-100">
                <Shield className="h-5 w-5 mr-2 text-blue-500" />
                Compliance
              </h3>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                  Physical mailing address included
                </li>
                <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                  Functional unsubscribe mechanism
                </li>
                <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                  Both HTML and plain text versions
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center text-gray-900 dark:text-gray-100">
                <AlertCircle className="h-5 w-5 mr-2 text-blue-500" />
                For AWS SES Review Team
              </h3>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  All templates shown here demonstrate our compliance with AWS
                  SES requirements for production access.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last Updated:{" "}
              {new Date().toLocaleDateString("en-AU", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Test Email Modal */}
      {currentTemplateData && (
        <SendTestEmailModal
          isOpen={isTestEmailModalOpen}
          onClose={() => setIsTestEmailModalOpen(false)}
          templateName={currentTemplateData.name}
          templateHtml={currentTemplateData.html}
          templateText={currentTemplateData.text}
        />
      )}
    </EmailPageLayout>
  );
}
